/**
 * The one place that talks HTTP.
 *
 * Deliberately minimal: no cookies (`credentials: "omit"`), no custom headers
 * beyond Content-Type, so a customer's site never trips a CORS preflight it
 * cannot satisfy, and nothing the SDK sends can carry a session of the
 * visitor's SnipCat login along with it.
 */

export interface ApiResponse {
  ok: boolean;
  status: number;
  data: Record<string, unknown> | null;
}

const FAILED: ApiResponse = { ok: false, status: 0, data: null };

export interface RequestOptions {
  /** Let the request outlive the page that started it (unload flush). */
  keepalive?: boolean;
  debug?: boolean;
}

/**
 * Sends one request and NEVER rejects: a tracking call that throws inside a
 * customer's promise chain is a bug in their page, not in their analytics.
 */
export const request = async (
  url: string,
  method: "POST" | "PATCH" | "GET",
  body: unknown,
  options: RequestOptions = {},
): Promise<ApiResponse> => {
  if (typeof fetch !== "function") {
    return FAILED;
  }
  try {
    const response = await fetch(url, {
      method,
      credentials: "omit",
      keepalive: options.keepalive === true,
      headers:
        method === "GET" ? undefined : { "Content-Type": "application/json" },
      body: method === "GET" ? undefined : JSON.stringify(body ?? {}),
    });

    let payload: Record<string, unknown> | null = null;
    try {
      payload = (await response.json()) as Record<string, unknown>;
    } catch {
      payload = null;
    }
    const result: ApiResponse = {
      ok: response.ok,
      status: response.status,
      // the API wraps its answer in {data: …}; unwrap it once, here
      data:
        payload && typeof payload === "object" && "data" in payload
          ? (payload as { data: Record<string, unknown> }).data ?? null
          : payload,
    };
    if (options.debug) {
      // eslint-disable-next-line no-console
      console.debug("[snipcat]", method, url, body, "->", result);
    }
    return result;
  } catch (e) {
    if (options.debug) {
      // eslint-disable-next-line no-console
      console.debug("[snipcat] request failed", url, e);
    }
    return FAILED;
  }
};
